/**
 * Schemas Zod para o bloco Assinatura (integração com provedor digital — Clicksign etc).
 *
 * Hoje só Clicksign está planejado, mas o schema permite múltiplos provedores
 * pra trocar sem reescrever (caso Clicksign vire caro/lento depois).
 */

import { z } from 'zod';

import { cpfSchema, emailSchema, isoTimestampSchema, textoCurtoSchema } from './common.js';

export const provedorAssinaturaSchema = z.enum(['clicksign', 'd4sign', 'zapsign']);

export const statusAssinaturaSchema = z.enum([
  'aguardando',
  'parcialmente-assinado',
  'completo',
  'cancelado',
  'expirado',
]);

export const statusAssinanteSchema = z.enum(['pendente', 'visualizou', 'assinou', 'recusou']);

export const papelAssinanteSchema = z.enum(['cliente', 'infomatic']);

export const assinanteStatusSchema = z.object({
  nome: textoCurtoSchema,
  email: emailSchema,
  cpf: cpfSchema.optional(),
  papel: papelAssinanteSchema,
  status: statusAssinanteSchema,
  assinadoEm: isoTimestampSchema.optional(),
  /** IP de origem (capturado pelo provedor) */
  ip: z.string().max(45).optional(),
});

export const assinaturaSchema = z.object({
  provedor: provedorAssinaturaSchema,
  /** ID do documento no provedor (Clicksign retorna como `key`) */
  documentoId: z.string().min(1).max(200),
  /** URL pública do documento (cliente clica pra assinar) */
  url: z.string().url(),
  status: statusAssinaturaSchema,
  assinantes: z.array(assinanteStatusSchema).min(1),
  enviadoEm: isoTimestampSchema,
  completadoEm: isoTimestampSchema.optional(),
  /** URL do PDF final assinado no Blob Storage (retenção legal 10 anos) */
  pdfBlobUrl: z.string().url().optional(),
});

// Tipo canônico é `Assinatura` em types/aceite.ts. Schema fica só pra validação runtime.
export type AssinaturaFromSchema = z.infer<typeof assinaturaSchema>;

/**
 * Payload mínimo pra criar assinatura (resto preenchido pelo provedor + servidor).
 * Usado internamente quando o backend dispara envio pra Clicksign.
 */
export const criarAssinaturaInputSchema = z.object({
  provedor: provedorAssinaturaSchema,
  assinantes: z
    .array(
      z.object({
        nome: textoCurtoSchema,
        email: emailSchema,
        cpf: cpfSchema.optional(),
        papel: papelAssinanteSchema,
      }),
    )
    .min(1),
});

export type CriarAssinaturaInput = z.infer<typeof criarAssinaturaInputSchema>;

/**
 * Eventos do webhook Clicksign (subset relevante).
 * Documentação: https://developers.clicksign.com/docs/webhooks-1
 */
export const webhookClicksignEventoSchema = z.enum([
  'document.created',
  'document.signed',
  'document.canceled',
  'document.refused',
  'document.deadline',
  'signer.viewed',
  'signer.signed',
  'auto_close',
]);

export type WebhookClicksignEvento = z.infer<typeof webhookClicksignEventoSchema>;
